import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { OptionButton, InventoryMenu, PlaythroughSnapshotMenuOptions } from '../../components/game';
import { AuthPageLayout, AuthForm } from '../../components/layout';
import { LoadingSpinner, Button, SlowResponseOverlay } from '../../components/ui';
import { CharacterPortrait } from '../../components/ui/CharacterPortrait';
import { AdventureBanner } from '../../components/ui/AdventureBanner';
import ToastContainer from '../../components/ui/ToastContainer';
import { pageStyles } from '../../config/theme';
import { RichTextRenderer } from '../../components/ui/RichTextRenderer';
import useUIStore from '../../stores/uiStore';
import { useGameState } from '../../hooks/useGameState';
import { useAdventureStyle } from '../../hooks/useAdventureStyle';
import useGameStore from '../../stores/gameStore';
import { unlockTimerAudio } from '../../hooks/useTimerAudioUnlock';
import useAuthStore from '../../stores/authStore';
import authService from '../../services/auth';
import guestService from '../../services/guestService';
import { useWaitingSceneRefresh } from '../../hooks/useWaitingSceneRefresh';
import { SNAPSHOT_MENU_SCENE_ID } from '../../constants/playthroughSnapshot';

interface RouteParams extends Record<string, string | undefined> {
  sessionId: string;
  playerId?: string;
}

/** Check if any section description contains a sync-option or the script that produces one. */
function sectionsHaveDescriptionInteractives(
  sections: Array<{ description?: string | { elements?: Array<{ type?: string; content?: unknown }> } }> | undefined
): boolean {
  if (!sections) return false;
  for (const section of sections) {
    const desc = section.description;
    if (!desc) continue;
    if (typeof desc === 'string') {
      if (desc.includes('getCharacterSynchronisationWaitOption')) return true;
      if (desc.includes('<Color-Picker')) return true;
      continue;
    }
    const elements = desc.elements ?? [];
    const check = (elts: typeof elements): boolean => {
      for (const el of elts) {
        if (el?.type === 'sync-option') return true;
        if (el?.type === 'color-picker') return true;
        if (el?.type === 'script' && typeof (el as { scriptCode?: string }).scriptCode === 'string' &&
            (el as { scriptCode: string }).scriptCode.includes('getCharacterSynchronisationWaitOption')) return true;
        if (Array.isArray((el as { content?: unknown }).content) && check((el as { content: typeof elements }).content)) return true;
        const withChildren = el as { children?: typeof elements; elseChildren?: typeof elements };
        if (Array.isArray(withChildren.children) && check(withChildren.children)) return true;
        if (Array.isArray(withChildren.elseChildren) && check(withChildren.elseChildren)) return true;
      }
      return false;
    };
    if (check(elements)) return true;
  }
  return false;
}

/** Server validation errors from apiService resolve to `{ message }` via axios interceptor. */
function selectActionErrorMessage(err: unknown, fallback: string): string {
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    const m = ((err as { message: string }).message || '').trim();
    if (m) return m;
  }
  return fallback;
}

const ScenePage: React.FC = () => {
  const { sessionId, playerId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const uiStore = useUIStore();
  const gameState = useGameState();
  const gameStore = useGameStore();
  const authStore = useAuthStore();
  const { buttonColor } = useAdventureStyle();
  useWaitingSceneRefresh(); // Visibility refresh, polling, registration timeout

  const flushPendingInputs = async () => {
    if (typeof gameStore.flushPendingUserInputs === 'function') {
      await gameStore.flushPendingUserInputs();
    }
  };

  const [inventoryMenuOpen, setInventoryMenuOpen] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isJoiningSession, setIsJoiningSession] = useState(false);
  const initializedRef = useRef<string | null>(null);

  // Initialize game state when component mounts or sessionId changes
  useEffect(() => {
    let cancelled = false;
    const setDebugIfMounted = (value: boolean) => {
      if (!cancelled) {
        setShowDebugInfo(value);
      }
    };

    const initializeGame = async () => {
      if (!sessionId) {
        return;
      }
      if (import.meta.env.DEV) {
        console.log('[ScenePage] initializeGame: effect ran', {
          urlSessionId: sessionId,
          urlPlayerId: playerId,
          storeSessionId: gameStore.currentSession?.id,
          storePlayerId: gameStore.currentPlayer?.id,
        });
      }

      // Get current state from store (read directly, don't use as dependency to avoid infinite loops)
      const currentSession = gameStore.currentSession;
      const currentPlayer = gameStore.currentPlayer;
      const currentScene = gameStore.currentScene;

      // Skip if we've already initialized for this sessionId AND the session/player/scene matches
      // This prevents unnecessary reloads when navigating to the same scene
      // Also skip if we have a scene loaded for this session (e.g., from createGuestSession)
      if (initializedRef.current === sessionId && 
          currentSession?.id === sessionId && 
          currentPlayer?.id === playerId &&
          currentScene) {
        gameStore.setStartAdventureTransitionInProgress(false);
        return;
      }
      
      // Also check if we have the session and player loaded but just need to load the scene
      if (currentSession?.id === sessionId && 
          currentPlayer?.id === playerId &&
          !currentScene) {
        try {
          await gameStore.loadCurrentScene();
          initializedRef.current = sessionId;
          gameStore.setStartAdventureTransitionInProgress(false);
          return;
        } catch (sceneError) {
          console.error('[ScenePage] initializeGame: Failed to load scene:', sceneError);
          // Fall through to full reload
        }
      }
      
      // If we're switching to a different session, clear the old state first
      // EXCEPT when we're in the middle of START_ADVENTURE: store just set the new session
      // but URL is still the old one; we must navigate to the new session instead of clearing.
      // Use store flag (not a ref) so it survives React Strict Mode remount.
      if (currentSession && currentSession.id !== sessionId) {
        const transitionInProgress = gameStore.startAdventureTransitionInProgress;
        if (import.meta.env.DEV) {
          console.log('[ScenePage] initializeGame: session mismatch', {
            urlSessionId: sessionId,
            urlPlayerId: playerId,
            storeSessionId: currentSession?.id,
            storePlayerId: currentPlayer?.id,
            startAdventureTransitionInProgress: transitionInProgress,
            willNavigateInsteadOfClear: transitionInProgress && !!currentPlayer,
          });
        }
        if (transitionInProgress && currentSession && currentPlayer) {
          if (import.meta.env.DEV) {
            console.log('[ScenePage] initializeGame: navigating to new session (START_ADVENTURE transition)', {
              sessionId: currentSession.id,
              playerId: currentPlayer.id,
            });
          }
          navigate(`/scene/${currentSession.id}/${currentPlayer.id}`, { replace: true });
          // Defer clearing the flag so a re-render from the store doesn't run this effect again
          // with the same (old) URL and transitionInProgress=false, which would clear the new session.
          queueMicrotask(() => gameStore.setStartAdventureTransitionInProgress(false));
          return;
        }
        if (import.meta.env.DEV) {
          console.log('[ScenePage] initializeGame: clearing session state (session mismatch, transition not in progress)');
        }
        gameStore.clearSessionState();
        // Wait for state to clear and reset the ref
        await new Promise(resolve => setTimeout(resolve, 100));
        if (cancelled) {
          return;
        }
        initializedRef.current = null; // Reset ref when switching sessions
      }
      
      // Mark this sessionId as initialized
      initializedRef.current = sessionId;
      
      try {
        // Check if we need to join the session
        // We need to join if:
        // 1. We don't have a current player, OR
        // 2. The current player doesn't belong to this session, OR
        // 3. We have a playerId in URL but it doesn't match current player, OR
        // 4. We don't have a current scene loaded
        const hasCurrentPlayer = !!currentPlayer;
        const hasCurrentSession = !!currentSession;
        const hasCurrentScene = !!currentScene;
        const sessionMatches = hasCurrentSession && currentSession?.id === sessionId;
        const playerMatches = !playerId || (hasCurrentPlayer && currentPlayer.id === playerId);
        
        const needsToJoin = !hasCurrentPlayer || !hasCurrentSession || !sessionMatches || !playerMatches || !hasCurrentScene;
        if (import.meta.env.DEV && needsToJoin) {
          console.log('[ScenePage] initializeGame: needsToJoin=true', {
            sessionId,
            playerId,
            hasCurrentPlayer,
            hasCurrentSession,
            sessionMatches,
            playerMatches,
            hasCurrentScene,
          });
        }

        if (needsToJoin) {
          setIsJoiningSession(true);
          try {
            // If we have a playerId in the URL, it means we've already joined (e.g., via invite or guest session)
            // In this case, load the session state directly instead of calling joinSession
            if (playerId) {
              if (import.meta.env.DEV) {
                console.log('[ScenePage] initializeGame: loading session state from URL', { sessionId, playerId });
              }
              // Load session state directly since we already have a player
              // Note: We don't have playerData here, so it will be looked up
              await gameStore.loadSessionState(sessionId, playerId);
              
              // Wait a bit for state to propagate
              await new Promise(resolve => setTimeout(resolve, 200));
              if (cancelled) {
                return;
              }

              // Read fresh state from store using getState() to get latest values
              // Zustand stores expose getState() method on the hook
              const storeState = (useGameStore as any).getState?.() || gameStore;
              const loadedScene = storeState.currentScene || gameStore.currentScene;
              
              // Ensure the scene is loaded - loadCurrentScene should have been called by loadSessionState
              // but let's make sure by calling it again if needed
              if (!loadedScene) {
                await gameStore.loadCurrentScene();
              }
              
              // Ensure isLoading is false and joining flag is cleared after successful load
              gameStore.setLoading(false);
              setIsJoiningSession(false);
            } else {
              // No playerId, so we need to join the session
              await gameState.joinSession(sessionId);
              gameStore.setLoading(false);
              setIsJoiningSession(false);
            }
          } catch (joinError) {
            console.error('[ScenePage] initializeGame: Failed to join/load session:', joinError);
            console.error('[ScenePage] initializeGame: Error details:', {
              message: joinError instanceof Error ? joinError.message : String(joinError),
              stack: joinError instanceof Error ? joinError.stack : undefined,
              sessionId,
              playerId
            });
            setDebugIfMounted(true);
            // Ensure isLoading is false even on error
            gameStore.setLoading(false);
            setIsJoiningSession(false);
            // Reset initialization on error so we can retry
            if (initializedRef.current === sessionId) {
              initializedRef.current = null;
            }
            // Don't navigate away - show debug info instead
            return;
          }
        } else {
          // No need to join - we already have everything
          // Ensure isLoading is false
          gameStore.setLoading(false);
          setIsJoiningSession(false);
        }
        
        // Verify we have a scene loaded (read from store again after potential updates)
        const updatedScene = gameStore.currentScene;
        if (!updatedScene) {
          try {
            await gameStore.loadCurrentScene();
          } catch (sceneError) {
            console.error('[ScenePage] initializeGame: Failed to load scene:', sceneError);
            setDebugIfMounted(true);
          }
        }
        
        // If we reach here and everything matches, we should not show debug info
        // The debug page should only show if there was an actual error
        
        // Final validation: if everything is working correctly, don't show debug
        // Read from store again to get latest state using getState() to avoid stale closures
        // Wait a bit more to ensure state has propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        if (cancelled) {
          return;
        }

        // Read fresh state directly from store using getState() to get latest values
        // Zustand stores expose getState() method on the hook
        const finalStoreState = (useGameStore as any).getState?.() || gameStore;
        const finalSession = finalStoreState.currentSession || gameStore.currentSession;
        const finalPlayer = finalStoreState.currentPlayer || gameStore.currentPlayer;
        const finalScene = finalStoreState.currentScene || gameStore.currentScene;
        
        const finalCheck = {
          hasSession: !!finalSession,
          hasPlayer: !!finalPlayer,
          hasScene: !!finalScene,
          sessionMatches: finalSession?.id === sessionId,
          playerMatches: !playerId || finalPlayer?.id === playerId
        };
        
        // If everything is correct, ensure debug is not shown and clear any stale transition flag
        if (finalCheck.hasSession && finalCheck.hasPlayer && finalCheck.hasScene && finalCheck.sessionMatches && finalCheck.playerMatches) {
          setDebugIfMounted(false);
          gameStore.setStartAdventureTransitionInProgress(false);
        } else {
          console.warn('[ScenePage] initializeGame: Validation failed, showing debug info', {
            missing: {
              session: !finalCheck.hasSession,
              player: !finalCheck.hasPlayer,
              scene: !finalCheck.hasScene,
              sessionMatch: !finalCheck.sessionMatches,
              playerMatch: !finalCheck.playerMatches
            },
            actual: {
              sessionId: finalSession?.id,
              playerId: finalPlayer?.id,
              sceneId: finalScene?.id
            },
            expected: {
              sessionId,
              playerId
            }
          });
          setDebugIfMounted(true);
        }
        
      } catch (err) {
        console.error('[ScenePage] initializeGame: Unexpected error:', err);
        console.error('[ScenePage] initializeGame: Error stack:', err instanceof Error ? err.stack : 'No stack trace');
        setDebugIfMounted(true);
        // Reset initialization on error so we can retry (but only for this sessionId)
        if (initializedRef.current === sessionId) {
          initializedRef.current = null;
        }
      }
    };

    void initializeGame();
    return () => {
      cancelled = true;
    };
  }, [sessionId, playerId]); // Only depend on URL params, not store values to avoid infinite loops

  // Handle post-join validation when game state updates
  useEffect(() => {
    if (isJoiningSession && gameState.currentPlayer && playerId) {
      if (gameState.currentPlayer.id !== playerId) {
        setShowDebugInfo(true);
      } else {
        setShowDebugInfo(false);
      }
      
      setIsJoiningSession(false);
    }
  }, [isJoiningSession, gameState.currentPlayer, playerId]);

  // Scroll to top when scene changes
  // This handles the case where new scenes are shorter than previous scenes
  useEffect(() => {
    if (gameState.currentScene) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      const scrollToTop = () => {
        // First, try instant scroll to ensure we get to the top regardless of content length
        window.scrollTo({ top: 0, behavior: 'auto' });
        
        // Then, if there's enough content, do a smooth scroll for better UX
        requestAnimationFrame(() => {
          // Check if we have enough content to scroll smoothly
          const documentHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );
          const windowHeight = window.innerHeight;
          
          // Only do smooth scroll if there's enough content
          if (documentHeight > windowHeight + 100) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      };
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(scrollToTop);
    }
  }, [gameState.currentScene?.id]); // Re-run when scene ID changes

  // Handle image loading errors in rich text content
  useEffect(() => {
    const handleImageError = (e: Event) => {
      const img = e.target as HTMLImageElement;
      if (img && (img.classList.contains('rich-text-image') || img.classList.contains('rich-text-portrait') || img.classList.contains('rich-text-icon'))) {
        console.warn(`Failed to load image: ${img.src}`);
        // Replace with a placeholder
        img.style.display = 'none';
        const placeholder = document.createElement('div');
        // Preserve the original classes
        placeholder.className = img.className;
        
        // Different styling for icons vs other images
        if (img.classList.contains('rich-text-icon')) {
          placeholder.style.cssText = `
            background-color: #374151;
            color: #9ca3af;
            padding: 0.5rem;
            text-align: center;
            border-radius: 0.375rem;
            margin: 0.5rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            display: inline-block;
            vertical-align: middle;
            min-width: 32px;
            min-height: 32px;
            font-size: 0.75rem;
          `;
          placeholder.textContent = '🎯';
        } else {
          placeholder.style.cssText = `
            background-color: #374151;
            color: #9ca3af;
            padding: 1rem;
            text-align: center;
            border-radius: 0.5rem;
            margin: 1rem 0;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          `;
          placeholder.textContent = '🖼️ Image not available';
        }
        
        img.parentNode?.insertBefore(placeholder, img);
      }
    };

    // Add event listeners to all images in rich text content
    const images = document.querySelectorAll('.rich-text-image, .rich-text-portrait, .rich-text-icon');
    images.forEach(img => {
      img.addEventListener('error', handleImageError);
    });

    // Cleanup function
    return () => {
      images.forEach(img => {
        img.removeEventListener('error', handleImageError);
      });
    };
  }, [gameState.currentScene]); // Re-run when scene changes

  // Get adventure title from session data
  const getAdventureTitle = () => {
    return gameState.currentAdventure?.title || gameState.currentSession?.adventure?.title || 'Adventure Scene';
  };

  // Handle navigation to system adventure
  const handleNavigateToSystemAdventure = async () => {
    try {
      uiStore.setLoading(true, 'Loading Nexus...');
      await flushPendingInputs();
      
      // Load or create system session
      const { sessionId, playerId } = await gameStore.loadSystemSession();
      
      // Navigate to system adventure
      navigate(`/scene/${sessionId}/${playerId}`);
    } catch (error) {
      console.error('Failed to navigate to system adventure:', error);
      uiStore.showToast('Failed to load Nexus. Please try again.', 'error');
      // Fallback to home page
      navigate('/');
    } finally {
      uiStore.setLoading(false);
    }
  };

  // Option target may be a script (e.g. '''"JOIN_ADVENTURE:" + getVariable(...)''') that is only
  // evaluated on the backend. Use REST path when target could evaluate to JOIN_ADVENTURE.
  const SCRIPT_DELIMITER = '\u0027\u0027\u0027'; // triple single quote
  const isJoinAdventureOption = (opt: { target?: string } | undefined) =>
    opt?.target != null &&
    (opt.target.startsWith('JOIN_ADVENTURE:') ||
      (opt.target.includes('JOIN_ADVENTURE') && (opt.target.includes(SCRIPT_DELIMITER) || opt.target.includes('getVariable'))));

  const isReturnToAdventureOption = (opt: { target?: string } | undefined) =>
    opt?.target != null &&
    (opt.target === 'RETURN_TO_ADVENTURE' ||
      (opt.target.includes('RETURN_TO_ADVENTURE') &&
        (opt.target.includes(SCRIPT_DELIMITER) || opt.target.includes('getVariable'))));

  // Resolve option from optionsMap or from currentScene.sections (optionsMap may have incomplete option data from API)
  const getOptionForSelect = (id: string) => {
    const fromMap = gameState.optionsMap[id];
    if (fromMap?.target != null) return fromMap;
    for (const section of gameState.currentScene?.sections ?? []) {
      const opt = (section.options ?? []).find((o: { id?: string }) => o.id === id);
      if (opt) return opt;
    }
    return fromMap ?? undefined;
  };

  // Handle option selection
  const handleOptionSelect = async (optionId: string) => {
    try {
      unlockTimerAudio();
      const option = getOptionForSelect(optionId);

      if (import.meta.env.DEV) {
        console.log('[ScenePage] handleOptionSelect', { optionId, target: option?.target, isJoinAdventure: isJoinAdventureOption(option) });
      }

      if (option?.target === 'MAIN_MENU') {
        await flushPendingInputs();
        
        // If the option has effects, we must execute them via backend first
        // This ensures effects (like rejectAdventureInvite) are processed before navigation
        if (option?.effect) {
          // Execute via backend to run effects - use gameStore directly to get the response
          try {
            const { currentSession, currentPlayer } = gameStore;
            if (currentSession && currentPlayer) {
              // Call the backend directly to execute effects
              await apiService.performAction(
                currentSession.id,
                currentPlayer.id,
                { action: 'select_option', optionId }
              );
              
              // Effects have been executed, now handle navigation
              // The backend returns action: 'MAIN_MENU' but we handle navigation here
            }
          } catch (error) {
            console.error('Failed to execute MAIN_MENU option effects:', error);
            // Continue with navigation even if effect execution fails
          }
        }
        
        // Handle navigation (either after effects executed or if no effects)
        // Check if we're in system adventure
        const isSystemAdventure = gameState.currentAdventure?.id === gameStore.systemAdventureId;
        
        if (isSystemAdventure) {
          // In system adventure, navigate to nexus scene
          await handleNavigateToScene('nexus');
        } else {
          // If we're currently viewing the adventure menu, close it first so the
          // return stack is restored to the active adventure scene before we leave.
          if (gameState.currentScene?.id === 'adventure_menu') {
            try {
              await handleNavigateToScene('adventure_menu');
            } catch (error) {
              console.warn('Failed to close adventure menu before MAIN_MENU navigation:', error);
            }
          }
          // Not in system adventure, load system session and navigate to it
          await handleNavigateToSystemAdventure();
        }
        return;
      }
      
      if (option?.target === 'QUIT_ADVENTURE') {
        await flushPendingInputs();
        // Check if we're in system adventure
        const isSystemAdventure = gameState.currentAdventure?.id === gameStore.systemAdventureId;
        
        if (isSystemAdventure) {
          // In system adventure, navigate to nexus instead of home
          await handleNavigateToScene('nexus');
        } else {
          // Regular adventure, navigate to home
          handleLeaveAdventure();
        }
        return;
      }
      
      if (option?.target === 'SIGN_OUT') {
        await flushPendingInputs();
        
        // If the option has effects, we must execute them via backend first
        if (option?.effect) {
          try {
            const { currentSession, currentPlayer } = gameStore;
            if (currentSession && currentPlayer) {
              // Call the backend directly to execute effects
              await apiService.performAction(
                currentSession.id,
                currentPlayer.id,
                { action: 'select_option', optionId }
              );
            }
          } catch (error) {
            console.error('Failed to execute SIGN_OUT option effects:', error);
            // Continue with sign out even if effect execution fails
          }
        }
        
        // Handle sign out
        await handleSignOut();
        return;
      }
      
      if (option?.target === 'DELETE_ACCOUNT') {
        await flushPendingInputs();
        
        // If the option has effects, we must execute them via backend first
        if (option?.effect) {
          try {
            const { currentSession, currentPlayer } = gameStore;
            if (currentSession && currentPlayer) {
              // Call the backend directly to execute effects
              await apiService.performAction(
                currentSession.id,
                currentPlayer.id,
                { action: 'select_option', optionId }
              );
            }
          } catch (error) {
            console.error('Failed to execute DELETE_ACCOUNT option effects:', error);
            // Continue with delete account even if effect execution fails
          }
        }
        
        // Handle delete account
        await handleDeleteAccount();
        return;
      }
      
      // Check if this is a START_ADVENTURE or START_OR_RESUME_ADVENTURE target
      const isStartAdventureTarget = option?.target?.startsWith('START_ADVENTURE:');
      const isStartOrResumeTarget = option?.target?.startsWith('START_OR_RESUME_ADVENTURE:');
      if (isStartAdventureTarget || isStartOrResumeTarget) {
        if (!option || !option.target) {
          uiStore.showToast('Invalid adventure target', 'error');
          return;
        }
        await flushPendingInputs();
        const prefix = isStartOrResumeTarget ? 'START_OR_RESUME_ADVENTURE:' : 'START_ADVENTURE:';
        const adventureId = option.target.substring(prefix.length).trim();
        
        if (!adventureId) {
          uiStore.showToast('Invalid adventure target', 'error');
          return;
        }
        
        // Use REST API directly (bypasses WebSocket)
        // This is necessary because these actions need to create or resume a session
        try {
          uiStore.setLoading(true, isStartOrResumeTarget ? 'Starting or resuming adventure...' : 'Starting adventure...');
          gameStore.setStartAdventureTransitionInProgress(true);

          await gameStore.selectOption(optionId);

          const state = useGameStore.getState();
          const { currentSession, currentPlayer } = state;
          if (currentSession && currentPlayer) {
            gameStore.setStartAdventureTransitionInProgress(false);
            navigate(`/scene/${currentSession.id}/${currentPlayer.id}`, { replace: true });
          } else {
            gameStore.setStartAdventureTransitionInProgress(false);
            console.error(`${prefix} completed but no new session found`, {
              hasSession: !!currentSession,
              hasPlayer: !!currentPlayer,
            });
            uiStore.showToast('Failed to start adventure', 'error');
          }
        } catch (error) {
          gameStore.setStartAdventureTransitionInProgress(false);
          console.error('Failed to start adventure:', error);
          uiStore.showToast('Failed to start adventure. Please try again.', 'error');
        } finally {
          uiStore.setLoading(false);
        }
        return;
      }
      
      // Check if this is a LOGGED_IN target (literal or script expression that resolves to LOGGED_IN)
      const isLoggedInOption =
        option?.target === 'LOGGED_IN' ||
        (typeof option?.target === 'string' &&
          option.target.includes('LOGGED_IN') &&
          (option.target.includes(SCRIPT_DELIMITER) || option.target.includes('getVariable')));
      if (isLoggedInOption) {
        // LOGGED_IN target detected
        await flushPendingInputs();
        
        // Use REST API directly for LOGGED_IN (bypasses WebSocket)
        // This navigates to the authenticated user's system adventure
        // Note: Tokens should already be updated via waiting-update event when login completed
        try {
          uiStore.setLoading(true, 'Signing in...');
          
          // Call gameStore.selectOption directly to use REST API
          // This will handle LOGGED_IN, get/create system session, and navigate to it
          await gameStore.selectOption(optionId);
          
          // After LOGGED_IN, the gameStore should have updated currentSession and currentPlayer
          // Wait for state to be fully loaded (loadSessionState is async)
          let retries = 0;
          const maxRetries = 30; // Increased retries
          while (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 200)); // Increased delay
            const { currentSession, currentPlayer } = gameStore;
            if (currentSession && currentPlayer) {
              // Session state available
              
              // Check if we're already at the correct URL
              const expectedUrl = `/scene/${currentSession.id}/${currentPlayer.id}`;
              const currentUrl = window.location.pathname;
              
              if (currentUrl !== expectedUrl) {
                // Use replace: true to replace the current history entry (guest session URL)
                // This ensures that if the page is reloaded, it will reload the new URL, not the old one
                navigate(expectedUrl, { replace: true });
              }
              break;
            }
            retries++;
          }
          
          // Check if we got a new session
          if (!gameStore.currentSession || !gameStore.currentPlayer) {
            console.error(`[ScenePage] LOGGED_IN: No session found after ${maxRetries} retries. currentSession=${gameStore.currentSession?.id}, currentPlayer=${gameStore.currentPlayer?.id}`);
            uiStore.showToast('Failed to navigate to system adventure', 'error');
          }
        } catch (error) {
          console.error('[ScenePage] LOGGED_IN: Error during navigation:', error);
          uiStore.showToast('Failed to navigate after login. Please try again.', 'error');
        } finally {
          uiStore.setLoading(false);
        }
        return;
      }
      
      // Use REST + navigate when: target is JOIN_ADVENTURE / RETURN_TO_ADVENTURE (literal or script), or target is missing
      // (backend may omit target in API response; it still returns JOIN_ADVENTURE and we navigate when session changes)
      const useJoinAdventurePath =
        isJoinAdventureOption(option) || isReturnToAdventureOption(option) || option?.target === undefined;
      if (useJoinAdventurePath) {
        await flushPendingInputs();

        if (import.meta.env.DEV) {
          console.log('[ScenePage] Using REST path for session switch (join / return / unknown target)', {
            optionId,
            target: option?.target,
          });
        }

        try {
          const sessionSwitchLoadingMessage =
            option?.target === 'RETURN_TO_ADVENTURE' || isReturnToAdventureOption(option)
              ? 'Returning...'
              : 'Joining adventure...';
          uiStore.setLoading(true, sessionSwitchLoadingMessage);
          // Same as START_ADVENTURE: prevent URL-sync effect from clearing new session (survives Strict Mode remount)
          gameStore.setStartAdventureTransitionInProgress(true);

          const prevSessionId = gameStore.currentSession?.id;
          await gameStore.selectOption(optionId);

          // Get fresh state via getState() - the hook's gameStore can have stale closure values
          const { currentSession, currentPlayer } = useGameStore.getState();
          if (import.meta.env.DEV) {
            console.log('[ScenePage] selectOption returned', {
              prevSessionId,
              currentSessionId: currentSession?.id,
              currentPlayerId: currentPlayer?.id,
            });
          }

          const sessionChanged = currentSession && currentSession.id !== prevSessionId;
          if (sessionChanged && currentSession && currentPlayer) {
            gameStore.setStartAdventureTransitionInProgress(false);
            navigate(`/scene/${currentSession.id}/${currentPlayer.id}`, { replace: true });
          } else if (sessionChanged === false && option?.target === undefined) {
            gameStore.setStartAdventureTransitionInProgress(false);
            // Target was missing and we stayed in same session – store already updated scene from response
            // No extra action needed unless we wanted to show an error for "expected JOIN_ADVENTURE"
          } else if (!currentSession || !currentPlayer) {
            gameStore.setStartAdventureTransitionInProgress(false);
            console.error('[ScenePage] No session/player after selectOption', { optionId });
            uiStore.showToast('Failed to join adventure', 'error');
          } else {
            gameStore.setStartAdventureTransitionInProgress(false);
          }
        } catch (error) {
          gameStore.setStartAdventureTransitionInProgress(false);
          console.error('[ScenePage] Failed to join adventure', error);
          uiStore.showToast('Failed to join adventure. Please try again.', 'error');
        } finally {
          uiStore.setLoading(false);
        }
        return;
      }
      
      const completed = await gameState.selectOption(optionId);
      
      // Check if this was a finish option and player completed the adventure
      if (completed) {
        const isSystemAdventure = gameState.currentAdventure?.id === gameStore.systemAdventureId;
        
        if (isSystemAdventure) {
          // In system adventure, navigate to nexus instead of home
          await handleNavigateToScene('nexus');
        } else {
          // Regular adventure, navigate to home
          gameState.clearSessionState();
          navigate('/');
        }
        return;
      }
      
    } catch (err) {
      // Error handling is done in the gameState hook
      console.error('Failed to select option:', err);
    }
  };

  // Handle inventory item selection
  const handleInventoryItemSelect = async (itemTag: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      // Use currentSession from gameState instead of URL params
      // This ensures we use the correct session when switching from system adventure
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;
      
      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select item. No active session or player.', 'error');
        return;
      }

      // Call the new API to select inventory item and transition to target scene
      await apiService.selectInventoryItem(currentSessionId, currentPlayerId, itemTag, targetScene);
      
      // Reload the current scene to get updated state
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select inventory item:', err);
      uiStore.showToast(
        selectActionErrorMessage(err, 'Failed to select item. Please try again.'),
        'error'
      );
    }
  };

  const handleActivityReferenceSelect = async (
    activityId: string,
    targetScene: string,
    variableName: string
  ) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select activity. No active session or player.', 'error');
        return;
      }

      await apiService.selectActivityReference(
        currentSessionId,
        currentPlayerId,
        activityId,
        variableName,
        targetScene
      );
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select activity reference:', err);
      uiStore.showToast(
        selectActionErrorMessage(err, 'Failed to select activity. Please try again.'),
        'error'
      );
    }
  };

  const handleGuestAdventureSelect = async (
    inviteId: string,
    targetScene: string,
    variableName: string
  ) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select guest. No active session or player.', 'error');
        return;
      }

      await apiService.selectGuestAdventureInvite(
        currentSessionId,
        currentPlayerId,
        inviteId,
        variableName,
        targetScene
      );
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select guest adventure invite:', err);
      uiStore.showToast(
        selectActionErrorMessage(err, 'Failed to select guest. Please try again.'),
        'error'
      );
    }
  };

  const handleKeyedListEntrySelect = async (
    entryId: string,
    targetScene: string,
    sourceVariable: string,
    variableName: string
  ) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select entry. No active session or player.', 'error');
        return;
      }

      await apiService.selectKeyedListEntry(
        currentSessionId,
        currentPlayerId,
        entryId,
        sourceVariable,
        variableName,
        targetScene
      );
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select keyed list entry:', err);
      uiStore.showToast(
        selectActionErrorMessage(err, 'Failed to select entry. Please try again.'),
        'error'
      );
    }
  };

  const handleColorOptionSelect = async (variableName: string, colorValue: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select color. No active session or player.', 'error');
        return;
      }

      await apiService.selectColorOption(currentSessionId, currentPlayerId, variableName, colorValue, targetScene);
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select color:', err);
      uiStore.showToast(
        selectActionErrorMessage(err, 'Failed to select color. Please try again.'),
        'error'
      );
    }
  };

  // Handle friend request selection
  const handleFriendRequestSelect = async (requestId: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      // Use currentSession from gameState instead of URL params
      // This ensures we use the correct session when switching from system adventure
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;
      
      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select friend request. No active session or player.', 'error');
        return;
      }

      // Import friendsService
      const friendsService = (await import('../../services/friendsService')).default;
      
      // Call the API to select friend request and transition to target scene
      await friendsService.selectFriendRequest(currentSessionId, currentPlayerId, requestId, targetScene);
      
      // Reload the current scene to get updated state (same pattern as inventory item selection)
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select friend request:', err);
      uiStore.showToast('Failed to select friend request. Please try again.', 'error');
    }
  };

  const handleFriendSelect = async (friendId: string, targetScene?: string) => {
    if (!targetScene) {
      uiStore.showToast('No target scene for friend selection.', 'error');
      return;
    }
    try {
      await flushPendingInputs();
      // Use currentSession from gameState instead of URL params
      // This ensures we use the correct session when switching from system adventure
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;
      
      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select friend. No active session or player.', 'error');
        return;
      }

      // Import friendsService
      const friendsService = (await import('../../services/friendsService')).default;
      
      // Call the API to select friend and transition to target scene
      await friendsService.selectFriend(currentSessionId, currentPlayerId, friendId, targetScene);
      
      // Reload the current scene to get updated state (same pattern as inventory item selection)
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select friend:', err);
      uiStore.showToast('Failed to select friend. Please try again.', 'error');
    }
  };

  const handleScheduleEventSelect = async (eventId: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select event. No active session or player.', 'error');
        return;
      }

      await apiService.selectScheduleEvent(currentSessionId, currentPlayerId, eventId, targetScene);
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select schedule event:', err);
      uiStore.showToast('Failed to select event. Please try again.', 'error');
    }
  };

  const handleScheduleEventTimeSelect = async (
    eventId: string,
    timeEpochSeconds: number,
    targetScene: string,
    realWorldLocation?: string
  ) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select event time. No active session or player.', 'error');
        return;
      }

      await apiService.selectScheduleEventTime(
        currentSessionId,
        currentPlayerId,
        eventId,
        timeEpochSeconds,
        targetScene,
        realWorldLocation
      );
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select schedule event time:', err);
      uiStore.showToast('Failed to select event time. Please try again.', 'error');
    }
  };

  const handleJoinEventSelect = async (eventId: string, startScene: string) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to join event. No active session or player.', 'error');
        return;
      }

      await apiService.selectScheduleEvent(currentSessionId, currentPlayerId, eventId, startScene);
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to join event:', err);
      uiStore.showToast('Failed to join event. Please try again.', 'error');
    }
  };

  const handleFeedbackCardSelect = async (feedbackId: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to open feedback detail. No active session or player.', 'error');
        return;
      }

      await apiService.selectFeedbackCard(currentSessionId, currentPlayerId, feedbackId, targetScene);
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to open feedback detail:', err);
      uiStore.showToast('Failed to open feedback detail. Please try again.', 'error');
    }
  };

  const handleSyncOptionSelect = async (
    syncVar: string,
    targetScene: string,
    characterNames?: string[],
    targetByCharacter?: Record<string, string>,
    syncEffects?: string
  ) => {
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select sync option. No active session or player.', 'error');
        return;
      }

      await apiService.selectSyncOption(
        currentSessionId,
        currentPlayerId,
        syncVar,
        targetScene,
        characterNames ?? [],
        targetByCharacter,
        syncEffects
      );
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select sync option:', err);
      uiStore.showToast('Failed to select sync option. Please try again.', 'error');
    }
  };

  // Handle adventure invite status selection (for Adventure-Invite-Status-List)
  const handleAdventureInviteStatusSelect = async (inviteId: string, targetScene: string) => {
    try {
      await flushPendingInputs();
      // Use currentSession from gameState instead of URL params
      // This ensures we use the correct session when switching from system adventure
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;
      
      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to select adventure invite. No active session or player.', 'error');
        return;
      }

      // Import adventureInvitesService
      const adventureInvitesService = (await import('../../services/adventureInvitesService')).default;
      
      // Call the API to select adventure invite and transition to target scene
      await adventureInvitesService.selectAdventureInvite(currentSessionId, currentPlayerId, inviteId, targetScene);
      
      // Reload the current scene to get updated state (same pattern as friend request selection)
      await gameState.loadCurrentScene();
    } catch (err) {
      console.error('Failed to select adventure invite:', err);
      uiStore.showToast('Failed to select adventure invite. Please try again.', 'error');
    }
  };

  // Handle adventure invite click - join the adventure
  const handleAdventureInviteClick = async (inviteId: string) => {
    try {
      await flushPendingInputs();
      uiStore.setLoading(true, 'Joining adventure...');
      
      // Import adventureInvitesService
      const adventureInvitesService = (await import('../../services/adventureInvitesService')).default;
      
      // Join the adventure from the invite
      const { sessionId: newSessionId, playerId: newPlayerId, player: joinedPlayer } = await adventureInvitesService.joinAdventureFromInvite(inviteId);
      
      // Clear session state before loading new state
      gameStore.clearSessionState();
      
      // Wait a moment for state to clear
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Load the session state directly using the player data we just got
      // This avoids the timing issue of looking up the player after it was just created
      try {
        await gameStore.loadSessionState(newSessionId, newPlayerId, joinedPlayer);
        
        // Get fresh state - hook's gameStore can have stale closure values after async updates
        const state = useGameStore.getState();
        
        // Verify the scene was actually loaded before navigating
        if (!state.currentScene) {
          throw new Error('Scene not loaded after loadSessionState');
        }
        
        // Verify the session and player match what we expect
        if (state.currentSession?.id !== newSessionId) {
          throw new Error('Session mismatch after loadSessionState');
        }
        
        if (state.currentPlayer?.id !== newPlayerId) {
          throw new Error('Player mismatch after loadSessionState');
        }
        
        // Mark this session as initialized BEFORE navigating so the useEffect doesn't try to reload it
        initializedRef.current = newSessionId;
        // Navigate to the new session
        navigate(`/scene/${newSessionId}/${newPlayerId}`, { replace: true });
      } catch (loadError) {
        console.error('[ScenePage] Failed to load session state:', loadError);
        console.error('[ScenePage] Error details:', {
          message: loadError instanceof Error ? loadError.message : String(loadError),
          stack: loadError instanceof Error ? loadError.stack : undefined,
          newSessionId,
          newPlayerId
        });
        // If loading fails, DON'T navigate - show error and let user retry
        initializedRef.current = null;
        uiStore.showToast('Failed to join adventure. Please try again.', 'error');
        // Don't navigate on error - stay on current page so user can retry
        return;
      }
      
      // The useEffect will detect the sessionId/playerId change
      // If we successfully loaded the state, it will see that everything matches and skip reloading
      // If we failed to load, it will retry with the retry logic
    } catch (err) {
      console.error('[ScenePage] Failed to join adventure from invite:', err);
      uiStore.showToast('Failed to join adventure. Please try again.', 'error');
    } finally {
      uiStore.setLoading(false);
    }
  };

  // Handle navigation to a specific scene
  const handleNavigateToScene = async (targetScene: string) => {
    if (gameState.isActionPending) return;
    try {
      await flushPendingInputs();
      // Use currentSession from gameState instead of URL params
      // This ensures we use the correct session when switching from system adventure
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;
      
      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to navigate. No active session or player.', 'error');
        return;
      }

      // Same pending/escalation as option click: disable options, then all interactions after 500ms, overlay after 3s
      gameState.beginPendingAction('Loading...');
      try {
        // Call the API to navigate to the target scene
        await apiService.navigateToScene(currentSessionId, currentPlayerId, targetScene);
        
        // Reload the current scene to get updated state
        await gameState.loadCurrentScene();
      } finally {
        uiStore.clearActionPending();
      }
    } catch (err) {
      console.error('Failed to navigate to scene:', err);
      uiStore.showToast('Failed to navigate. Please try again.', 'error');
      uiStore.clearActionPending();
    }
  };

  const handleActivityRedoNavigate = async (redoSceneId: string, activityId: string) => {
    if (gameState.isActionPending) return;
    try {
      await flushPendingInputs();
      const currentSessionId = gameState.currentSession?.id || sessionId;
      const currentPlayerId = gameState.currentPlayer?.id;

      if (!currentSessionId || !currentPlayerId) {
        console.error('No session or player ID available');
        uiStore.showToast('Failed to redo activity. No active session or player.', 'error');
        return;
      }

      gameState.beginPendingAction('Loading...');
      try {
        await apiService.navigateToActivityRedo(currentSessionId, currentPlayerId, redoSceneId, activityId);
        await gameState.loadCurrentScene();
      } finally {
        uiStore.clearActionPending();
      }
    } catch (err) {
      console.error('Failed activity redo navigation:', err);
      uiStore.showToast('Failed to redo activity. Please try again.', 'error');
      uiStore.clearActionPending();
    }
  };

  // Handle navigation
  const handleNavigateHome = async () => {
    await flushPendingInputs();
    // Clear session state when navigating home to prevent stale data
    gameState.clearSessionState();
    navigate('/');
  };

  // Helper function to reset initialization ref
  const resetInitialization = () => {
    initializedRef.current = null;
  };

  // Handle sign out - signs out user and creates new guest session
  const handleSignOut = async () => {
    try {
      uiStore.setLoading(true, 'Signing out...');
      await flushPendingInputs();
      
      // Disconnect WebSocket if active
      if (gameState.websocket.isConnected) {
        gameState.websocket.disconnect({ force: true });
      }
      
      // CRITICAL: Clear game store session state FIRST to prevent restoring old session
      gameStore.clearSessionState();
      
      // Clear old guest data (in case there was any)
      guestService.clearGuestData();
      
      // CRITICAL: Create new guest session BEFORE clearing auth state
      // This ensures we have guest tokens ready before ProtectedRoute sees isAuthenticated: false
      const guestSession = await apiService.createGuestSession(gameStore.systemAdventureId);
      
      // CRITICAL: Set guest tokens BEFORE clearing user auth state
      // This sets isGuest: true and isAuthenticated: true, preventing ProtectedRoute redirect
      authStore.setGuestTokens(
        guestSession.token,
        guestSession.refreshToken,
        guestSession.guestUserId
      );
      
      // Now clear the user data (setUser(null) but keep guest state from setGuestTokens)
      // Don't call authStore.logout() as it would set isGuest: false and trigger redirect
      authStore.setUser(null);
      
      // Call authService.logout() to invalidate user tokens on server
      // But don't use authStore.logout() which would clear guest state
      try {
        await authService.logout();
      } catch (error) {
        // Continue even if server logout fails
        console.warn('Server logout call failed:', error);
      }
      
      // Wait a moment for state to propagate and ensure ProtectedRoute sees isGuest: true
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // CRITICAL: Reset initialization ref to ensure new session loads properly
      resetInitialization();
      
      // Navigate to new guest session
      navigate(`/scene/${guestSession.session.id}/${guestSession.player.id}`, { replace: true });
      
      // Wait a moment to ensure navigation completes
      await new Promise(resolve => setTimeout(resolve, 300));
      
      uiStore.showToast('Signed out successfully', 'success');
    } catch (error) {
      console.error('Failed to sign out:', error);
      uiStore.showToast('Failed to sign out. Please try again.', 'error');
      // Fallback to home page
      navigate('/');
    } finally {
      uiStore.setLoading(false);
    }
  };

  // Handle delete account - deletes user account and creates new guest session
  // First deletes the account (while we have the user token), then uses sign-out process for navigation
  const handleDeleteAccount = async () => {
    try {
      uiStore.setLoading(true, 'Deleting account...');
      await flushPendingInputs();
      
      // Step 1: Delete the account from the backend (requires user token, so do this first)
      try {
        await authService.deleteAccount();
      } catch (deleteError: any) {
        // If deletion fails, show error and stop
        console.error('Failed to delete account:', deleteError);
        const errorMessage = deleteError?.response?.data?.message || deleteError?.message || 'Failed to delete account. Please try again.';
        uiStore.showToast(errorMessage, 'error');
        return; // Don't proceed with sign-out if deletion failed
      }
      
      // Step 2: Use the sign-out process to create guest session and navigate
      // This reuses the working sign-out flow for consistency
      // Note: We've already deleted the account, so the user token will be invalid,
      // but sign-out handles this gracefully
      // We need to call handleSignOut but skip its loading management since we're already managing it
      try {
        console.log('[handleDeleteAccount] Starting sign-out process after account deletion');
        
        // Temporarily disable loading management in handleSignOut by not calling it directly
        // Instead, extract the sign-out logic but skip the loading state changes
        await flushPendingInputs();
        
        // Disconnect WebSocket if active
        if (gameState.websocket.isConnected) {
          console.log('[handleDeleteAccount] Disconnecting WebSocket');
          gameState.websocket.disconnect({ force: true });
        }
        
        // CRITICAL: Clear game store session state FIRST to prevent restoring old session
        console.log('[handleDeleteAccount] Clearing game store session state');
        gameStore.clearSessionState();
        
        // Clear old guest data (in case there was any)
        console.log('[handleDeleteAccount] Clearing old guest data');
        guestService.clearGuestData();
        
        // CRITICAL: Create new guest session BEFORE clearing auth state
        // This ensures we have guest tokens ready before ProtectedRoute sees isAuthenticated: false
        console.log('[handleDeleteAccount] Creating new guest session');
        const guestSession = await apiService.createGuestSession(gameStore.systemAdventureId);
        console.log('[handleDeleteAccount] Guest session created:', {
          sessionId: guestSession.session.id,
          playerId: guestSession.player.id,
          guestUserId: guestSession.guestUserId
        });
        
        // CRITICAL: Set guest tokens BEFORE clearing user auth state
        // This sets isGuest: true and isAuthenticated: true, preventing ProtectedRoute redirect
        console.log('[handleDeleteAccount] Setting guest tokens');
        authStore.setGuestTokens(
          guestSession.token,
          guestSession.refreshToken,
          guestSession.guestUserId
        );
        
        // Now clear the user data (setUser(null) but keep guest state from setGuestTokens)
        // Don't call authStore.logout() as it would set isGuest: false and trigger redirect
        console.log('[handleDeleteAccount] Clearing user data');
        authStore.setUser(null);
        
        // Call authService.logout() to invalidate user tokens on server (will fail gracefully if account is deleted)
        try {
          await authService.logout();
        } catch (error) {
          // Continue even if server logout fails (expected if account is already deleted)
          console.warn('[handleDeleteAccount] Server logout call failed (expected after account deletion):', error);
        }
        
        // Wait a moment for state to propagate and ensure ProtectedRoute sees isGuest: true
        console.log('[handleDeleteAccount] Waiting for state propagation');
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // CRITICAL: Reset initialization ref to ensure new session loads properly
        console.log('[handleDeleteAccount] Resetting initialization ref');
        resetInitialization();
        
        // Navigate to new guest session
        const targetUrl = `/scene/${guestSession.session.id}/${guestSession.player.id}`;
        console.log('[handleDeleteAccount] Navigating to:', targetUrl);
        navigate(targetUrl, { replace: true });
        
        // Wait a moment to ensure navigation completes
        console.log('[handleDeleteAccount] Waiting for navigation to complete');
        await new Promise(resolve => setTimeout(resolve, 300));
        
        console.log('[handleDeleteAccount] Account deletion and navigation completed successfully');
        uiStore.showToast('Account deleted successfully', 'success');
      } catch (signOutError: any) {
        // If sign-out fails, we still need to create a guest session and navigate
        // This is a fallback to ensure the user isn't stuck
        console.error('[handleDeleteAccount] Sign-out failed after account deletion, attempting fallback:', signOutError);
        
        try {
          // Fallback: manually create guest session and navigate
          console.log('[handleDeleteAccount] Fallback: Clearing game store session state');
          gameStore.clearSessionState();
          console.log('[handleDeleteAccount] Fallback: Clearing guest data');
          guestService.clearGuestData();
          console.log('[handleDeleteAccount] Fallback: Creating guest session');
          const guestSession = await apiService.createGuestSession(gameStore.systemAdventureId);
          console.log('[handleDeleteAccount] Fallback: Guest session created:', {
            sessionId: guestSession.session.id,
            playerId: guestSession.player.id
          });
          console.log('[handleDeleteAccount] Fallback: Setting guest tokens');
          authStore.setGuestTokens(
            guestSession.token,
            guestSession.refreshToken,
            guestSession.guestUserId
          );
          console.log('[handleDeleteAccount] Fallback: Clearing user data');
          authStore.setUser(null);
          await new Promise(resolve => setTimeout(resolve, 200));
          console.log('[handleDeleteAccount] Fallback: Resetting initialization');
          resetInitialization();
          const targetUrl = `/scene/${guestSession.session.id}/${guestSession.player.id}`;
          console.log('[handleDeleteAccount] Fallback: Navigating to:', targetUrl);
          navigate(targetUrl, { replace: true });
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('[handleDeleteAccount] Fallback: Navigation completed');
          uiStore.showToast('Account deleted successfully', 'success');
        } catch (fallbackError) {
          console.error('[handleDeleteAccount] Fallback guest session creation also failed:', fallbackError);
          uiStore.showToast('Account deleted, but failed to create guest session. Please refresh the page.', 'error');
          navigate('/');
        }
      }
    } catch (error: any) {
      console.error('[handleDeleteAccount] Failed to delete account:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete account. Please try again.';
      uiStore.showToast(errorMessage, 'error');
      // Don't navigate away on error - let user try again or cancel
    } finally {
      uiStore.setLoading(false);
    }
  };

  const handleLeaveAdventure = async () => {
    try {
      await flushPendingInputs();
      // "Leave Adventure" should close the adventure for all players
      // This marks the adventure as closed but preserves it for review
      if (sessionId) {
        await apiService.closeSession(sessionId);
        uiStore.showToast('Adventure closed successfully', 'success');
      }
      // Clear session state when leaving adventure
      gameState.clearSessionState();
      navigate('/');
    } catch (err) {
      console.error('Failed to leave adventure:', err);
      uiStore.showToast('Failed to leave adventure. Please try again.', 'error');
    }
  };

  // Debug logging for render state (removed verbose logs)

  // Loading component (only show full-page when we have no scene yet; avoid flicker when option or navigate is pending)
  // When isActionPending, keep showing current scene even if isLoading is true or currentScene is briefly cleared (e.g. guest path)
  const showFullPageLoading = (gameState.isLoading || isJoiningSession) && !gameState.currentScene && !gameState.isActionPending;
  if (showFullPageLoading) {
    if (import.meta.env.DEV) {
      console.log('[ScenePage] Showing full-page Loading Scene', {
        gameStateIsLoading: gameState.isLoading,
        isJoiningSession,
        hasCurrentScene: !!gameState.currentScene,
        currentSceneId: gameState.currentScene?.id,
        gameStateIsActionPending: gameState.isActionPending,
        condition: {
          loadingOrJoining: gameState.isLoading || isJoiningSession,
          noScene: !gameState.currentScene,
          notActionPending: !gameState.isActionPending,
        },
      });
    }
    return (
      <AuthPageLayout
        title="Loading Scene"
        subtitle="Please wait while we load your current scene..."
        useAdventureGradient={true}
      >
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div style={pageStyles.formContainer}>
            <LoadingSpinner />
            <p style={{
              ...pageStyles.subtitle,
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              Loading adventure scene...
            </p>
            <p style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              textAlign: 'center',
              marginTop: '0.5rem'
            }}>
              {isJoiningSession ? 'Joining session...' : 'Loading scene...'}
            </p>
          </div>
        </AuthForm>
      </AuthPageLayout>
    );
  }

  // Debug info component
  if (showDebugInfo) {
    return (
      <AuthPageLayout
        title="Scene Debug Information"
        subtitle="Debug information for session/player mismatch"
        useAdventureGradient={true}
      >
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div style={pageStyles.formContainer}>
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '1rem'
              }}>
                🔍 Debug Information
              </h2>
              
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>URL Parameters:</h3>
                <div style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: '#c7d2fe'
                }}>
                  <div>Session ID: {sessionId || 'undefined'}</div>
                  <div>Player ID: {playerId || 'undefined'}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>Current Game State:</h3>
                <div style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: '#c7d2fe'
                }}>
                  <div>Current Session: {gameState.currentSession?.id || 'null'}</div>
                  <div>Current Player: {gameState.currentPlayer?.id || 'null'}</div>
                  <div>Current Adventure: {gameState.currentAdventure?.title || 'null'}</div>
                  <div>Is Loading: {gameState.isLoading ? 'true' : 'false'}</div>
                  <div>Has Error: {gameState.error ? 'true' : 'false'}</div>
                  {gameState.error && (
                    <div style={{ marginTop: '0.5rem', color: '#f87171' }}>
                      Error: {gameState.error.message || String(gameState.error)}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>Analysis:</h3>
                <div style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#c7d2fe'
                }}>
                  {!sessionId && <div style={{ color: '#f87171' }}>❌ No Session ID in URL</div>}
                  {!playerId && <div style={{ color: '#f87171' }}>❌ No Player ID in URL</div>}
                  {sessionId && gameState.currentSession?.id !== sessionId && <div style={{ color: '#fbbf24' }}>⚠️ Session ID mismatch: URL={sessionId}, Current={gameState.currentSession?.id}</div>}
                  {playerId && gameState.currentPlayer?.id !== playerId && <div style={{ color: '#fbbf24' }}>⚠️ Player ID mismatch: URL={playerId}, Current={gameState.currentPlayer?.id}</div>}
                  {sessionId && playerId && gameState.currentSession?.id === sessionId && gameState.currentPlayer?.id === playerId && <div style={{ color: '#34d399' }}>✅ Session and Player IDs match</div>}
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => {
                  setShowDebugInfo(false);
                  initializedRef.current = null;
                }}
                variant="glass-primary"
                className="flex-1"
              >
                🔄 Retry Loading
              </Button>
              <Button
                onClick={() => {
                  gameState.clearSessionState();
                  navigate('/');
                }}
                variant="glass-secondary"
                className="flex-1"
              >
                🏠 Return to Home
              </Button>
            </div>
          </div>
        </AuthForm>
      </AuthPageLayout>
    );
  }

  // Error component
  if (gameState.error) {
    return (
      <AuthPageLayout
        title="Scene Loading Error"
        subtitle="We encountered an error while loading your scene"
        useAdventureGradient={true}
      >
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div style={pageStyles.formContainer}>
            <div style={{
              backgroundColor: 'rgba(254, 226, 226, 0.1)',
              border: '1px solid rgba(254, 202, 202, 0.3)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
              <h2 style={{
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                Failed to load scene
              </h2>
              <p style={{
                color: '#c7d2fe',
                fontSize: '0.875rem',
                marginBottom: '1rem'
              }}>
                {gameState.error.message}
              </p>
            </div>
            
            <Button
              onClick={() => window.location.reload()}
              variant="glass-primary"
              className="w-full"
            >
              Retry
            </Button>
          </div>
        </AuthForm>
      </AuthPageLayout>
    );
  }

  if (!gameState.currentScene) {
    return (
      <AuthPageLayout
        title="Scene Not Found"
        subtitle="Unable to locate scene information..."
        useAdventureGradient={true}
      >
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div style={pageStyles.formContainer}>
            <p style={{
              ...pageStyles.subtitle,
              textAlign: 'center'
            }}>
              Waiting for scene information...
            </p>
          </div>
        </AuthForm>
      </AuthPageLayout>
    );
  }

  const currentScene = gameState.currentScene;

  const hideHeaderForGuests =
    gameState.currentAdventure?.hideHeaderForGuests === true
    && (authStore.isGuest || guestService.isGuest());

  // Get current player's character portrait ID
  const getCurrentPortraitId = () => {
    if (gameState.currentPlayer?.portraitId) {
      return gameState.currentPlayer.portraitId;
    }

    const characterName = gameState.currentPlayer?.characterName;
    if (!characterName || !gameState.currentAdventure) return undefined;
    
    // Find the character in the adventure to get their portraitId
    const character = gameState.currentAdventure.characters?.find(
      c => c.name === characterName
    );
    return character?.portraitId;
  };

  return (
    <>
      <AuthPageLayout
        title={hideHeaderForGuests ? undefined : (
          <div
            style={{
              boxSizing: 'border-box',
              width: '100%',
              height: 64,
              minHeight: 64,
              maxHeight: 64,
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <div
              style={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                gap: 8,
                width: '100%',
                height: '100%',
                minHeight: 0,
              }}
            >
              <div
                onClick={() => {
                  if (!gameState.isActionPending && !gameState.disableAllInteractions) {
                    handleNavigateToScene('character_menu');
                  }
                }}
                style={{
                  boxSizing: 'border-box',
                  cursor: gameState.disableAllInteractions ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  width: 64,
                  minWidth: 64,
                  height: '100%',
                  minHeight: 0,
                  pointerEvents: gameState.disableAllInteractions ? 'none' : 'auto',
                  opacity: gameState.disableAllInteractions ? 0.6 : 1,
                }}
                title="Character Menu"
                aria-disabled={gameState.disableAllInteractions}
              >
                <CharacterPortrait
                  portraitId={getCurrentPortraitId()}
                  variant="thumbnail"
                  alt={gameState.currentPlayer?.characterName || 'Character'}
                  data-testid="character-portrait-header"
                  fillContainer
                />
              </div>
              <div
                onClick={() => {
                  if (!gameState.isActionPending && !gameState.disableAllInteractions) {
                    handleNavigateToScene('adventure_menu');
                  }
                }}
                style={{
                  boxSizing: 'border-box',
                  cursor: gameState.disableAllInteractions ? 'not-allowed' : 'pointer',
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  minHeight: 0,
                  pointerEvents: gameState.disableAllInteractions ? 'none' : 'auto',
                  opacity: gameState.disableAllInteractions ? 0.6 : 1,
                }}
                title="Open Adventure Menu"
                data-testid="adventure-banner-header"
                aria-disabled={gameState.disableAllInteractions}
              >
                <AdventureBanner
                  bannerId={gameState.currentAdventure?.bannerId}
                  adventureId={
                    gameState.currentAdventure?.id
                    ?? gameStore.selectedAdventureId
                    ?? gameState.currentSession?.adventureId
                    ?? undefined
                  }
                  variant="thumbnail"
                  alt={getAdventureTitle()}
                  fillContainer
                />
              </div>
            </div>
          </div>
        )}
        useAdventureGradient={true}
      >
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div style={sceneStyles.container}>
            {/* Render Multiple Description-Option Sections */}
            {currentScene.sections.map((section, sectionIndex) => {
              // Find available options for this section
              const sectionOptions = (gameState.availableOptionsBySection || []).find(
                sectionData => sectionData.sectionIndex === sectionIndex
              )?.options || [];
              

              return (
                <div
                  key={`${currentScene.id}-section-${sectionIndex}`}
                  style={sceneStyles.sectionContainer}
                >
                  {/* Section Description */}
                  {section.description && (
                    <div style={sceneStyles.descriptionContainer}>
                      <RichTextRenderer
                        key={`${currentScene.id}-section-${sectionIndex}-description`}
                        description={section.description}
                        onInlineOptionClick={handleOptionSelect}
                        onInventoryItemSelect={handleInventoryItemSelect}
                        onColorOptionSelect={handleColorOptionSelect}
                        onFriendRequestSelect={handleFriendRequestSelect}
                        onFriendSelect={handleFriendSelect}
                        onScheduleEventSelect={handleScheduleEventSelect}
                        onScheduleEventTimeSelect={handleScheduleEventTimeSelect}
                        onJoinEventSelect={handleJoinEventSelect}
                        onFeedbackCardSelect={handleFeedbackCardSelect}
                        onSyncOptionSelect={handleSyncOptionSelect}
                        onAdventureInviteClick={handleAdventureInviteClick}
                        onAdventureInviteStatusSelect={handleAdventureInviteStatusSelect}
                        onGuestAdventureSelect={handleGuestAdventureSelect}
                        onKeyedListEntrySelect={handleKeyedListEntrySelect}
                        excludeUserIds={(gameStore.sessionPlayers ?? gameState.players)
                          ?.map((p: { userId?: string }) => p.userId)
                          .filter((id): id is string => Boolean(id))}
                        className="rich-text"
                        style={sceneStyles.descriptionText}
                        customColor={buttonColor}
                        disableInlineOptions={gameState.disableAllInteractions}
                        sessionId={sessionId}
                        activityListContext={{
                          onNavigate: (startSceneId) => {
                            void handleNavigateToScene(startSceneId);
                          },
                          onActivityRedo: (redoSceneId, activityId) => {
                            void handleActivityRedoNavigate(redoSceneId, activityId);
                          },
                          onActivityReferenceSelect: handleActivityReferenceSelect,
                          disableInteractions: gameState.disableAllInteractions,
                        }}
                      />
                    </div>
                  )}

                  {/* Section Options */}
                  {sectionOptions.length > 0 && (
                    <div style={sceneStyles.sectionOptionsContainer}>
                      {sectionOptions.map((option) => {
                        // Check if this is a FinishOption by looking at the target field
                        const isFinishOption = option.target === 'FINISH';
                        const isDisabled = gameState.isLoading || gameState.disableAllInteractions || (isFinishOption && gameState.currentPlayer?.completed);
                        
                        return (
                          <div key={option.id} style={sceneStyles.optionContainer}>
                            <OptionButton
                              text={option.text}
                              onClick={() => handleOptionSelect(option.id)}
                              disabled={isDisabled}
                              waiting={gameState.isActionPending}
                              variant="glass-primary"
                              fullWidth
                              customColor={buttonColor}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {gameState.currentScene.id === SNAPSHOT_MENU_SCENE_ID && (
              <PlaythroughSnapshotMenuOptions
                disabled={gameState.disableAllInteractions}
                waiting={gameState.isActionPending}
                customColor={buttonColor}
              />
            )}

            {/* Waiting Options */}
            {gameState.waitingOptions.length > 0 && (
              <div style={sceneStyles.waitingOptionsSection}>
                <h3 style={sceneStyles.sectionTitle}>Waiting for...</h3>
                <div style={sceneStyles.optionsList}>
                  {gameState.waitingOptions.map((option, index) => (
                    <div key={`waiting_${index}`} style={sceneStyles.optionContainer}>
                      <OptionButton
                        text={option.waitMessage || 'Waiting for other players...'}
                        onClick={() => {}} // No action for waiting options
                        disabled={true}
                        waiting={true}
                        variant="glass-secondary"
                        fullWidth
                        customColor={buttonColor}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Player Completion Status */}
            {gameState.currentPlayer?.completed && (
              <div style={sceneStyles.completionContainer}>
                <div style={sceneStyles.completionIcon}>✅</div>
                <h3 style={sceneStyles.completionTitle}>Adventure Complete!</h3>
                <p style={sceneStyles.completionText}>
                  You have successfully completed this adventure. The session will remain active for other players.
                </p>
                <Button
                  onClick={() => {
                    const isSystemAdventure = gameState.currentAdventure?.id === gameStore.systemAdventureId;
                    if (isSystemAdventure) {
                      handleNavigateToScene('nexus');
                    } else {
                      handleNavigateHome();
                    }
                  }}
                  variant="glass-primary"
                  className="w-full"
                >
                  {gameState.currentAdventure?.id === gameStore.systemAdventureId ? '🏠 Return to Nexus' : '🏠 Return to Home'}
                </Button>
              </div>
            )}


            {/* Adventure End State - Dead End (no options available) */}
            {(gameState.availableOptionsBySection || []).every(section => section.options.length === 0) && 
             gameState.waitingOptions.length === 0 &&
             !sectionsHaveDescriptionInteractives(gameState.currentScene?.sections) && (
              <div style={sceneStyles.endContainer}>
                <div style={sceneStyles.endIcon}>⚠️</div>
                <h3 style={sceneStyles.endTitle}>Dead End</h3>
                <p style={sceneStyles.endText}>
                  This scene has no available options to continue. This is a bug in the adventure and should be reported to the adventure creator.
                </p>
                <Button
                  onClick={() => {
                    const isSystemAdventure = gameState.currentAdventure?.id === gameStore.systemAdventureId;
                    if (isSystemAdventure) {
                      handleNavigateToScene('nexus');
                    } else {
                      handleNavigateHome();
                    }
                  }}
                  variant="glass-primary"
                  className="w-full"
                >
                  {gameState.currentAdventure?.id === gameStore.systemAdventureId ? '🏠 Return to Nexus' : '🏠 Return to Home'}
                </Button>
              </div>
            )}
          </div>
        </AuthForm>
      </AuthPageLayout>

      {/* Scene Menus - Rendered outside AuthPageLayout to avoid z-index issues */}
      <InventoryMenu
        isOpen={inventoryMenuOpen}
        onClose={() => setInventoryMenuOpen(false)}
        sessionId={sessionId}
        playerId={gameState.currentPlayer?.id}
      />

      {/* Slow response overlay – shown after ~3 s with no response (Phase 5) */}
      <SlowResponseOverlay isOpen={gameState.showProblemOverlay} />
      
      <ToastContainer />
    </>
  );
};

// Scene-specific styles matching the glass morphism theme
const sceneStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.25rem',
  },
  
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    marginBottom: '0.3rem',
  },
  
  descriptionContainer: {
    padding: '0',
    marginBottom: '0.5rem',
  },
  
  descriptionText: {
    color: '#ffffff',
    fontSize: '1rem',
    lineHeight: '1.6',
  },
  
  sectionOptionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  
  allOptionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  
  optionsSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  
  waitingOptionsSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
  },
  
  optionsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  
  optionContainer: {
    // Container for each option button
  },
  
  endContainer: {
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    border: '1px solid rgba(255, 193, 7, 0.3)',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    textAlign: 'center' as const,
  },
  
  endIcon: {
    fontSize: '2rem',
    marginBottom: '0.75rem',
  },
  
  endTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '0.5rem',
  },
  
  endText: {
    fontSize: '0.875rem',
    color: '#c7d2fe',
    marginBottom: '1rem',
  },

  completionContainer: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    textAlign: 'center' as const,
  },

  completionIcon: {
    fontSize: '2rem',
    marginBottom: '0.75rem',
  },

  completionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '0.5rem',
  },

  completionText: {
    fontSize: '0.875rem',
    color: '#c7d2fe',
    marginBottom: '1rem',
  },
};

export default ScenePage;